import { css } from "@emotion/css";
import { Badge, Tooltip } from "@octopusdeploy/design-system-components";
import { ExperimentIcon, FingerprintIcon, MessageIcon, RocketIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { type LinkHref, links } from "@octopusdeploy/portal-routes";
import type * as React from "react";
import { ComplianceReportCard } from "~/areas/platformHub/complianceReports/ComplianceReportCard";
import { PageContent } from "~/components/PageContent/PageContent";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { isChameleonCampaignActive } from "~/inAppMessaging/isChameleonCampaignActive";
import { showChameleonExperience } from "~/inAppMessaging/showChameleonExperience";
interface ComplianceReport {
    id: string;
    name: string;
    description: string;
    href: LinkHref | undefined; // undefined means we don't have a report built yet
    icon: React.ReactNode;
}
const complianceReports: ComplianceReport[] = [
    {
        id: "who-can-deploy",
        name: "Deployment Permissions",
        description: "Users with permissions to deploy to projects and environments.",
        href: links.deploymentPermissionsReportPage.generateUrl(),
        icon: <FingerprintIcon size={24} color="white"/>,
    },
    {
        id: "who-did-deploy",
        name: "Production Deployments",
        description: "Who deployed to production and when those deployments occurred.",
        href: undefined,
        icon: <RocketIcon size={24} color="white"/>,
    },
];
export async function complianceReportsPageLoader(): Promise<ComplianceReportsLoaderData> {
    // https://app.chameleon.io/surveys/69097c6c7119aa00306e7bda
    const isAuditReportFeedbackMicrosurveyActive = isChameleonCampaignActive(auditReportFeedbackMicrosurveyName, 5000);
    return {
        isAuditReportFeedbackMicrosurveyActive: await isAuditReportFeedbackMicrosurveyActive,
    };
}
interface ComplianceReportsLoaderData {
    isAuditReportFeedbackMicrosurveyActive: boolean;
}
interface ComplianceReportsPageProps {
    loaderData: ComplianceReportsLoaderData;
}
export function ComplianceReportsPage({ loaderData }: ComplianceReportsPageProps) {
    const alphaBadge = (<div className={styles.badgeContainer}>
            <Tooltip content={"This feature is not finished or fully tested."}>
                <Badge variant={"feature"} size={"medium"} label={"Alpha"} icon={<ExperimentIcon size={20} color={themeTokens.color.badge.icon.feature}/>}/>
            </Tooltip>
        </div>);
    return (<PageContent header={{ title: complianceReportsPageTitle, chip: alphaBadge }}>
            <div className={styles.container}>
                {complianceReports.map((report) => (<ComplianceReportCard key={report.id} header={report.name} description={report.description} icon={report.icon} href={report.href}/>))}
                {useOctopusFeatureToggle("compliance-reports-chameleon-survey", false) && loaderData.isAuditReportFeedbackMicrosurveyActive && (<ComplianceReportCard key="request a report" header="Request a report" description="Suggest a new report type or feature." icon={<MessageIcon size={24} color="white"/>} onClick={() => showChameleonExperience(auditReportFeedbackMicrosurveyName)}/>)}
            </div>
        </PageContent>);
}
export const complianceReportsPageTitle = "Reports";
const styles = {
    container: css({
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
        gap: space[32],
        padding: space[16],
    }),
    badgeContainer: css({
        lineHeight: "0",
    }),
};
const auditReportFeedbackMicrosurveyName = "Platform Hub - Audit Report Feedback";
